import "../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import React from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAvailableFlags } from './events-provider';
type FlagContextValue = {
  flagName: string;
};
const FlagContext = React.createContext<FlagContextValue | undefined>(undefined);

/**
 * Fires PostHog's $feature_flag_called exposure event when the user is shown this variant.
 * For flags originally configured as boolean in PostHog, converts 'variant'/'control'
 * back to true/false so PostHog's experiment dashboard matches the original flag type.
 *
 * Each hook instance tracks the last flag+variant key it fired for, so repeated
 * effect runs with the same inputs (parent re-renders, dep reference shuffles)
 * don't re-fire. A genuine exposure change (flag assignment flips mid-session)
 * produces a new key and fires correctly.
 */
const useFlagExposure = (flagName: string, variant: string): void => {
  const {
    flags,
    booleanFlags
  } = useAvailableFlags();
  const posthog = usePostHog();
  const flagValue = flags[flagName];
  const lastFiredKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (flagValue !== variant) return;
    const key = `${flagName}:${variant}`;
    if (lastFiredKeyRef.current === key) return;
    const rawValue = booleanFlags.includes(flagName) ? variant === 'variant' : variant;
    posthog?.capture('$feature_flag_called', {
      $feature_flag: flagName,
      $feature_flag_response: rawValue
    });
    lastFiredKeyRef.current = key;
  }, [flagName, flagValue, variant, booleanFlags, posthog]);
};
interface FlagSetProps {
  flagName: string;
  children: React.ReactNode;
}
const FlagSet: React.FC<FlagSetProps> = ({
  flagName,
  children
}) => {
  const contextValue = React.useMemo(() => ({
    flagName
  }), [flagName]);
  return <FlagContext.Provider value={contextValue}>
            {children}
        </FlagContext.Provider>;
};
interface FlagProps {
  children: React.ReactNode;
  variant: 'control' | 'variant' | string;
}
const Flag: React.FC<FlagProps> = ({
  children,
  variant
}) => {
  const ctx = React.useContext(FlagContext);
  if (!ctx) {
    throw new Error('<Flag> must be used inside <FlagSet>');
  }
  const {
    flagName
  } = ctx;
  useFlagExposure(flagName, variant);
  return <span data-test-variant={`${flagName}_${variant}`} className="flags__span-1obg8s">
            {children}
        </span>;
};
const ComposedBlockFlag: React.FC<FlagProps & FlagContextValue> = ({
  children,
  flagName,
  variant
}) => {
  // Only fire for the variant the user is actually seeing (CSS hides the others)
  useFlagExposure(flagName, variant);
  return <span data-test-variant={`${flagName}_${variant}`} className="flags__span-1obg8s">
            {children}
        </span>;
};
export { FlagSet, Flag, ComposedBlockFlag };